import React, { useEffect, useState } from "react";
import { Grid, Paper } from "@material-ui/core";
import {
  KButton,
  KSelect,
  KSelectAutocomplete,
  KTextInput,
} from "@kyburz/kdesign-system";
import KSwitch from "../../../components/ui/button/switch";
import {
  getConnectorDeereStart,
  postConnectorDeereReset,
} from "../../../apiCalls/admin/connectorDeereAuth";
import ExportToSettings from "./ExportToSettings/ExportToSettings";
import { getVehicleTemplates } from "../../../apiCalls/admin/vehicleTemplates";
import LSVASettings from "./lsvaSettings/LSVASettings";
import {useStyles} from "./styles";

interface CO2Values {
  petrol_per_l: number;
  diesel_per_l: number;
  gas_per_l: number;
  electric_per_kwh: number;
}

interface ConnectorStatus {
  access_token: boolean;
  refresh_token: boolean;
  code: boolean;
  valid: boolean;
}

interface Props {
  customer: string;
  handleToggle: (v) => void;
  handleSelectChange: (v, v1?) => void;
  handleTextInputChange: (arg0: any) => void;
  handleCO2ValueChange: (arg0: any) => void;
  sendNotifications: boolean;
  supportNumber: string;
  keylessAccessExtraTimeMinutes: number;
  maxReservationTokenHours: number;
  filterVehiclesByTrackingDevice: boolean;
  tripsCutAtMidnight: boolean;
  prefillVehicleDataByVIN: boolean;
  createDriverAutomatically: boolean;
  createDriverByDetectedId: boolean;
  externalIdField: string;
  editExternalIdField: boolean;
  showCanReserveFlag: boolean;
  co2Values: CO2Values;
  display_attachments: string;
  connectorStatus: ConnectorStatus;
  handleConnectorChange: () => void;
  exportOptions: string[];
  handleFieldChange: (name, value) => void;
  defaultTemplateId: string;
  handleDefaultTemplateChange: (
    _name: string,
    template: {
      value: string;
      label: string;
    },
  ) => void;
  internals: Record<string, any>;
  handleInternalsChange: (v?) => void;
}

const externalIdFieldIds: { label: string; value: string }[] = [
  {
    label: "VIN",
    value: "vin",
  },
  { label: "Inventory Number", value: "inventory_number" },
  { label: "License Plate", value: "license_plate" },
];

const displayAttachmentsOptions = [
  {
    label: "On: next to vehicle",
    value: "on",
  },
  { label: "Off: as own vehicle", value: "off" },
  {
    label: "Auto: as own vehicle if not attached",
    value: "auto",
  },
];

const BasicSettings = ({
  customer,
  handleToggle,
  handleSelectChange,
  handleTextInputChange,
  handleCO2ValueChange,
  sendNotifications,
  supportNumber,
  keylessAccessExtraTimeMinutes,
  maxReservationTokenHours,
  filterVehiclesByTrackingDevice,
  tripsCutAtMidnight,
  prefillVehicleDataByVIN,
  createDriverAutomatically,
  createDriverByDetectedId,
  externalIdField,
  editExternalIdField,
  co2Values,
  showCanReserveFlag,
  display_attachments,
  connectorStatus,
  handleConnectorChange,
  exportOptions = [],
  handleFieldChange,
  defaultTemplateId,
  handleDefaultTemplateChange,
  internals,
  handleInternalsChange,
}: Props) => {
  const classes = useStyles();
  const [vehicleTemplates, setVehicleTemplates] = useState<
    { label: string; value: string }[]
  >([]);

  useEffect(() => {
    getVehicleTemplates(customer).then((res) => {
      const mappedTemplates: { label: string; value: string }[] =
        res?.data?.data.map((x) => ({
          label: x?.name || "",
          value: x?._id || "",
        }));
      setVehicleTemplates(mappedTemplates);
    });
  }, []);

  const translatedField = externalIdFieldIds.find(
    (field) => field.value === externalIdField,
  )?.label;

  const handleStart = () => {
    getConnectorDeereStart(customer)
      .then((res) => {
        const url = res?.oauth2url;
        console.log(`redirect to: ${url}`);
        window.open(url, "_blank");
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const handleReset = () => {
    postConnectorDeereReset(customer)
      .then((res) => {
        console.log(res);
        handleConnectorChange();
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const attachmentsValue = displayAttachmentsOptions.find(
    ({ value }) => value === display_attachments,
  )?.label;

  const handleChangeDisplayAttachments = (value) =>
    handleSelectChange("display_attachments", value);

  return (
    <div className={classes.root}>
      <Grid container spacing={6}>
        <Grid item xs={12} md={8}>
          <Paper className={classes.paper}>
            <div className={classes.header}>Settings</div>
            <Grid container spacing={6}>
              <Grid item xs={12} md={6}>
                <KTextInput
                  onChange={handleTextInputChange}
                  value={supportNumber}
                  label="Technical Support Number"
                  placeholder="+41 12 345 67 89"
                  name="supportNumber"
                />
                <KSelectAutocomplete
                  placeholder="Value"
                  isClearable={!editExternalIdField}
                  name="externalIdField"
                  elements={externalIdFieldIds}
                  onChange={handleSelectChange}
                  value={translatedField || ""}
                  label="External Id Field"
                  style={{ width: 240 }}
                  disabled={editExternalIdField}
                />
                <KSelect
                  label="Display Attachments"
                  value={attachmentsValue}
                  onSelect={handleChangeDisplayAttachments}
                  elements={displayAttachmentsOptions}
                  name="display_attachments"
                  height={120}
                />
                <KTextInput
                  onChange={handleTextInputChange}
                  value={keylessAccessExtraTimeMinutes}
                  label="Keyless Access Extra Time (in minutes)"
                  placeholder="0"
                  name="keylessAccessExtraTimeMinutes"
                  type="number"
                  error={keylessAccessExtraTimeMinutes < 0}
                  errorText="Should be > 0"
                />
                <KTextInput
                  onChange={handleTextInputChange}
                  value={maxReservationTokenHours || undefined}
                  label="Keyless Access Token Limit (in hours)"
                  helperText="Set to 0 to disable"
                  placeholder="0"
                  name="maxReservationTokenHours"
                  type="number"
                  error={maxReservationTokenHours < 0}
                  errorText="Should be >= 0"
                />
                <KSelectAutocomplete
                  placeholder="Default template ID"
                  name="templateId"
                  elements={vehicleTemplates}
                  onChange={handleDefaultTemplateChange}
                  value={
                    vehicleTemplates?.find(
                      (t) => t?.value === defaultTemplateId,
                    )?.label || defaultTemplateId
                  }
                  label="Default template ID"
                  style={{ width: 240 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <KSwitch
                  name="createDriverAutomatically"
                  onChange={handleToggle}
                  checked={createDriverAutomatically}
                  label="Create Driver When Creating A User"
                />
                <KSwitch
                  name="createDriverByDetectedId"
                  onChange={handleToggle}
                  checked={createDriverByDetectedId}
                  label="Create Driver when a new ID is detected by a Fleet Value"
                />
                <KSwitch
                  name="sendNotifications"
                  onChange={handleToggle}
                  checked={sendNotifications}
                  label="Send Notifications"
                />
                <KSwitch
                  name="filterVehiclesByTrackingDevice"
                  onChange={handleToggle}
                  checked={filterVehiclesByTrackingDevice}
                  label="Filter Vehicles By Tracking Device"
                />
                <KSwitch
                  name="showCanReserveFlag"
                  onChange={handleToggle}
                  checked={showCanReserveFlag}
                  label="Show can reserve Toggle"
                />
                <KSwitch
                  name="tripsCutAtMidnight"
                  onChange={handleToggle}
                  checked={tripsCutAtMidnight}
                  label="Trips Cut At Midnight"
                />
                <KSwitch
                  name="prefillVehicleDataByVIN"
                  onChange={handleToggle}
                  checked={prefillVehicleDataByVIN}
                  label="Prefill vehicle data by VIN"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={classes.paper}>
            <div className={classes.header}>LSVA</div>
            <LSVASettings
              internals={internals}
              setInternals={handleInternalsChange}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper className={classes.paper}>
            <ExportToSettings
              exportOptions={exportOptions}
              setExportOptions={(options) =>
                handleFieldChange("exportTo", options)
              }
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={classes.paper}>
            <div className={classes.header}>Connectors</div>
            <div className={classes.connectorsStatus}>
              John Deere:
              {connectorStatus?.access_token && connectorStatus?.valid
                ? "valid access token"
                : connectorStatus?.code
                ? "no valid access token - code present"
                : "no valid access token - no code present"}
            </div>
            <div>
              <KButton
                theme="tertiary"
                children="reload"
                onClick={handleConnectorChange}
                className={classes.connectorsActionsButton}
              />
              <KButton
                theme="primary"
                children="start auth"
                disabled={
                  (connectorStatus?.access_token && connectorStatus?.valid) ||
                  connectorStatus.code
                }
                onClick={handleStart}
                className={classes.connectorsActionsButton}
              />
              <KButton
                theme="danger"
                children="reset"
                onClick={handleReset}
                className={classes.connectorsActionsButton}
              />
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={classes.paper}>
            <div className={classes.header}>CO2 emissions</div>
            <KTextInput
              onChange={handleCO2ValueChange}
              value={co2Values.petrol_per_l}
              type="number"
              label="kg per L petrol"
              placeholder="2.37"
              name="petrol_per_l"
            />
            <KTextInput
              onChange={handleCO2ValueChange}
              value={co2Values.diesel_per_l}
              type="number"
              label="kg per L diesel"
              placeholder="2.65"
              name="diesel_per_l"
            />
            <KTextInput
              onChange={handleCO2ValueChange}
              value={co2Values.gas_per_l}
              type="number"
              label="kg per L gas"
              placeholder="1.63"
              name="gas_per_l"
            />
            <KTextInput
              onChange={handleCO2ValueChange}
              value={co2Values.electric_per_kwh}
              type="number"
              label="kg per kWh (electric)"
              placeholder="0.005"
              name="electric_per_kwh"
            />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default BasicSettings;
